import env from '../../environments/environment'
import axios from 'axios'
import AuthService from '../services/Auth.service'
import ActivityStore from '../stores/activity.store'
import UploadService from './Upload.service'
import { attachAppCheck } from '../../api/FirebaseAuth'

const baseUrl = env.api.base_url

async function refreshToken() {
  return new Promise(async (resolve, reject) => {
    try {
      await AuthService.refreshToken()
      resolve()
    } catch (ex) {
      if (ex === 'NO_USER') window.location.href = '/login'
      else reject(`${ex}`)
    }
  })
}

function getInstance() {
  ActivityStore.recordRequest()
  return attachAppCheck(
    axios.create({
      accept: 'application/json',
      baseURL: baseUrl,
      timeout: 60000,
      headers: { Authorization: `Bearer ${AuthService.getToken() || ''}` },
    })
  )
}

function getMultipartInstance() {
  ActivityStore.recordRequest()
  return attachAppCheck(
    axios.create({
      accept: 'multipart/form-data',
      baseURL: baseUrl,
      timeout: 60000,
      headers: { Authorization: `Bearer ${AuthService.getToken() || ''}` },
    })
  )
}

class CommissionPaymentsService {
  /**
   * Get Career agent sales for a specific Elite Agency Team and month
   */
  async getCareerSales(teamId, year, month) {
    let instance = getInstance()
    try {
      const response = await instance.get('/admin/commission-payments/career-sales', {
        params: {
          team_id: teamId,
          year: year,
          month: month,
        },
      })
      return response.data
    } catch (e) {
      if (e && e.response && e.response.status === 403) {
        await refreshToken()
        instance = getInstance()
        const response = await instance.get('/admin/commission-payments/career-sales', {
          params: {
            team_id: teamId,
            year: year,
            month: month,
          },
        })
        return response.data
      }
      console.error('Error fetching Career agent sales:', e)
      throw e
    }
  }

  /**
   * Download Career agent sales as CSV
   */
  async downloadCsv(teamId, year, month) {
    let instance = getInstance()
    try {
      const response = await instance.get('/admin/commission-payments/download-csv', {
        params: {
          team_id: teamId,
          year: year,
          month: month,
        },
      })
      return response.data
    } catch (e) {
      if (e && e.response && e.response.status === 403) {
        await refreshToken()
        instance = getInstance()
        const response = await instance.get('/admin/commission-payments/download-csv', {
          params: {
            team_id: teamId,
            year: year,
            month: month,
          },
        })
        return response.data
      }
      console.error('Error downloading CSV:', e)
      throw e
    }
  }

  /**
   * Upload a CSV file to Firebase Storage
   */
  async uploadFile(file) {
    try {
      const formData = new FormData()
      formData.append('upload_type', 'commission_csv')
      formData.append('commission_csv', file, file.name)
      formData.append('filename', file.name)

      const response = await UploadService.upload(formData)
      return response
    } catch (e) {
      console.error('Error uploading file:', e)
      throw e
    }
  }

  /**
   * Process uploaded CSV and update commission_paid values
   */
  async processCsv(filePath) {
    let instance = getInstance()
    try {
      const response = await instance.post('/admin/commission-payments/upload-csv', {
        file_path: filePath,
      })
      return response.data
    } catch (e) {
      if (e && e.response && e.response.status === 403) {
        await refreshToken()
        instance = getInstance()
        const response = await instance.post('/admin/commission-payments/upload-csv', {
          file_path: filePath,
        })
        return response.data
      }
      console.error('Error processing CSV:', e)
      throw e
    }
  }

  /**
   * Get referral partner payments for a specific BA team, year and month
   */
  async getReferralPartnerPayments(teamId, year, month) {
    let instance = getInstance()
    try {
      const response = await instance.get('/admin/commission-payments/referral-payments', {
        params: {
          team_id: teamId,
          year: year,
          month: month,
        },
      })
      return response.data
    } catch (e) {
      if (e && e.response && e.response.status === 403) {
        await refreshToken()
        instance = getInstance()
        const response = await instance.get('/admin/commission-payments/referral-payments', {
          params: {
            team_id: teamId,
            year: year,
            month: month,
          },
        })
        return response.data
      }
      console.error('Error fetching referral partner payments:', e)
      throw e
    }
  }

  /**
   * Download referral partner payments as CSV
   */
  async downloadReferralPartnerPaymentsCsv(teamId, year, month) {
    let instance = getInstance()
    try {
      const response = await instance.get('/admin/commission-payments/referral-payments/download-csv', {
        params: {
          team_id: teamId,
          year: year,
          month: month,
        },
      })
      return response.data
    } catch (e) {
      if (e && e.response && e.response.status === 403) {
        await refreshToken()
        instance = getInstance()
        const response = await instance.get('/admin/commission-payments/referral-payments/download-csv', {
          params: {
            team_id: teamId,
            year: year,
            month: month,
          },
        })
        return response.data
      }
      console.error('Error downloading referral partner payments CSV:', e)
      throw e
    }
  }

  /**
   * Process uploaded CSV and update referral partner payment values
   */
  async processReferralPartnerPaymentsCsv(filePath) {
    let instance = getInstance()
    try {
      const response = await instance.post('/admin/commission-payments/referral-payments/upload-csv', {
        file_path: filePath,
      })
      return response.data
    } catch (e) {
      if (e && e.response && e.response.status === 403) {
        await refreshToken()
        instance = getInstance()
        const response = await instance.post('/admin/commission-payments/referral-payments/upload-csv', {
          file_path: filePath,
        })
        return response.data
      }
      console.error('Error processing referral partner payments CSV:', e)
      throw e
    }
  }
}

export default new CommissionPaymentsService()
